/**
 * Data Quality API — the tenant-wide abnormality-flag queue (Prompt 11F.1).
 *
 * Any process that discovers data it cannot safely auto-correct (migration
 * adapters today; future import/reconciliation jobs) writes a flag here
 * instead of silently defaulting or dropping data. Owners/admins work the
 * queue down; a flag targeted at one person also shows up in their own
 * "mine" view.
 */

import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiCall, ApiError } from '@core/api/client';

export type DataQualitySeverity = 'info' | 'warning' | 'critical';
export type DataQualityStatus = 'open' | 'resolved' | 'dismissed';

export interface DataQualityFlag {
  id: string;
  tenant_id: string;
  category: string;
  entity_type: string;
  entity_id: string | null;
  entity_label: string;
  title: string;
  detail: string;
  suggested_action?: string | null;
  severity: DataQualitySeverity;
  status: DataQualityStatus;
  target_type: 'role' | 'user';
  target_value: string;
  source: string;
  created_at: string;
  resolved_at?: string | null;
  resolved_by_uid?: string | null;
  resolution_note?: string | null;
}

export interface DataQualityFlagListResponse {
  items: DataQualityFlag[];
  /** Count of `items` in THIS response, not the collection-wide total. */
  total: number;
  /** Open flags among `items`. A floor, not the true total, when `truncated`. */
  open_count: number;
  /** True when the queue exceeds the server's page cap and this is a slice. */
  truncated?: boolean;
}

interface DecisionBody {
  note?: string;
}

function toastApiError(err: unknown, fallback: string): void {
  toast.error(err instanceof ApiError ? err.message || fallback : fallback);
}

export const dataQualityKeys = {
  queue: (status?: string) => ['data-quality', 'queue', status] as const,
  mine: (status?: string) => ['data-quality', 'mine', status] as const,
};

/** Owner/admin queue — all flags for the tenant, optionally filtered by status. */
export function useDataQualityQueue(status?: DataQualityStatus, enabled = true) {
  return useQuery({
    queryKey: dataQualityKeys.queue(status),
    queryFn: () =>
      apiCall<DataQualityFlagListResponse>('GET', '/v1/admin/data-quality', undefined, {
        query: { status },
      }),
    staleTime: 30_000,
    enabled,
  });
}

/** Lightweight open-count poll for the sidebar badge. Pass `enabled=false`
 * for any non owner/admin session — the endpoint 403s for them, and there's
 * no reason to fire the request. */
export function useDataQualityOpenCount(enabled = true): string {
  const { data } = useDataQualityQueue('open', enabled);
  const count = data?.open_count ?? 0;
  // Render "500+" rather than a flat "500" when the server truncated: the
  // count only describes the returned slice, and a badge that silently caps
  // out under-reports exactly when the queue most needs attention.
  return data?.truncated ? `${count}+` : String(count);
}

/** Flags targeted at the calling user specifically (any role). */
export function useMyDataQualityFlags(status?: DataQualityStatus) {
  return useQuery({
    queryKey: dataQualityKeys.mine(status),
    queryFn: () =>
      apiCall<DataQualityFlagListResponse>('GET', '/v1/data-quality/mine', undefined, {
        query: { status },
      }),
    staleTime: 30_000,
  });
}

export function useResolveDataQualityFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecisionBody }) =>
      apiCall<DataQualityFlag>('POST', `/v1/admin/data-quality/${id}/resolve`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['data-quality'] });
      toast.success('Marked resolved.');
    },
    onError: (err) => toastApiError(err, 'Failed to resolve flag'),
  });
}

export function useDismissDataQualityFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecisionBody }) =>
      apiCall<DataQualityFlag>('POST', `/v1/admin/data-quality/${id}/dismiss`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['data-quality'] });
      toast.success('Dismissed.');
    },
    onError: (err) => toastApiError(err, 'Failed to dismiss flag'),
  });
}
